"use client";

/**
 * SegmentedPicker — the canonical "row of pills, one selected" picker
 * used across Settings (and anywhere else with the same shape).
 *
 * ## Why this exists (v9.145)
 *
 * Pre-v9.145 the same control was hand-rolled in 9 files
 * (unit-toggle, bodyweight-unit-toggle, distance-unit-toggle,
 * unit-case-toggle, count-warmups-toggle, rest-increment-picker,
 * urgent-threshold-picker, session-zoom-picker, unit toggles).
 * Each reimplemented the same scaffolding: `role="radiogroup"`,
 * `role="radio"` buttons, the chrome (`rounded-md border bg-surface-2
 * overflow-hidden`), the active/inactive state styling, the
 * `pending && active` disable, and the optimistic-flip behaviour.
 *
 * Drift that accumulated across the 9:
 * - 7 of 9 had explicit `h-9` (the Sprint 22 v9.12 Settings-
 *   densification trim); 2 missed it and stayed on `px-4 py-2`
 *   natural height (a 1–2 px visual delta — small but inconsistent).
 * - Active state was `bg-brand-soft text-brand-text` (v9.93 muted) on all
 *   9 — that part stayed in sync.
 * - Padding split: `px-4` for 2-3 char labels (KG/LB, KG/LB samples),
 *   `px-3` for single-letter / numeric labels (Y/N, "10s", "Off").
 *
 * This component bakes in the invariants and exposes the legitimate
 * variants (layout, padding, extras) as props.
 *
 * ## Invariants (NOT configurable — would regress drift if exposed)
 *
 * - Container chrome: `rounded-md border border-border bg-surface-2
 *   overflow-hidden text-sm`
 * - Button height: `h-9` (Settings densification minimum)
 * - Active state: `text-fg border-b-2 border-brand` (v9.147
 *   underline-accent — see "Active style history" below)
 * - Inactive state: `text-fg-dim hover:text-fg border-b-2
 *   border-transparent` (transparent border reserves the 2 px so
 *   active/inactive buttons stay the same height)
 * - Transition: `transition-colors` on every button
 * - Disabled: `pending && active` — locks the just-picked button
 *   during in-flight write, leaves siblings tappable for retry
 * - Semantics: `role="radiogroup"` + `role="radio"` + `aria-checked`
 *
 * ## Active style history
 *
 * v9.84/v9.93 introduced a muted `bg-brand-soft text-brand-text` active
 * state across all picker surfaces, trying to stop preference
 * controls from competing with primary CTAs for attention. That
 * worked in the active-workout context (where the workout-header
 * toggles use a `border-b-2 border-brand` underline) but left
 * Settings sitting in an awkward middle tier — neither clearly
 * "this is selected" (vs the loud bg-brand still on
 * bodyweight-unit-pills) nor unambiguously quiet chrome. The
 * result was 3 active-state variants across the app.
 *
 * v9.146 first tried collapsing Settings to the LOUD
 * `bg-brand text-on-brand` fill (matching the bodyweight widget), but
 * the result read too aggressive for the dense settings page.
 *
 * v9.147 collapsed to the underline accent across Settings + the
 * workout-header toggles. v9.148 then propagated the same accent to
 * the remaining hand-rolled selector surfaces (Sessions zoom,
 * bodyweight unit pills, range pickers, etc.) so every segmented
 * picker in the app shares one active-state language:
 *   **`text-fg border-b-2 border-brand`** on the selected option,
 *   inactive options stay dim with a transparent reserve border.
 *
 * v9.151 (current) adds the `size` variant so the bodyweight widget
 * (previously its own component `bodyweight-unit-pills.tsx`) can use
 * SegmentedPicker directly:
 * - `size="standard"` (default): `h-9 [buttonPadding] text-sm` —
 *   the Settings density used by all 9 wrappers.
 * - `size="compact"`: `px-3 py-1.5 text-[11px]` — the dashboard /
 *   detail-page inline density used by the bodyweight unit pills
 *   (KG/LB/ST) and the Sessions heatmap zoom pills.
 *
 * ## Out of scope
 *
 * - **Workout-header toggles** (`workout-unit-toggle`,
 *   `workout-distance-unit-toggle`) — keep their existing
 *   underline-accent implementation (smaller `h-7`, `aria-pressed`
 *   semantics inside `role="group"`). They share the v9.147 active-
 *   style language with this component but use sizing one tier
 *   smaller than `compact`, tuned for the dense active-workout
 *   chrome. Candidate for future migration when SegmentedPicker
 *   grows a `size="mini"` variant or absorbs the `aria-pressed`
 *   semantics — not required for the v9.151 scope.
 */

type SegmentedPickerOption<T> = {
  /** Stable identity for this option — used as the React key, the
   *  comparison against `value`, and the argument to `onSelect`. */
  value: T;
  /** Visible label rendered inside the button. */
  label: string;
  /** Optional aria-label override — use when the visible label is
   *  ambiguous to screen readers (single-letter "Y"/"N",
   *  sample-pair "KG / LB", etc.). Falls back to no override, so
   *  the visible text reads naturally for assistive tech that
   *  surfaces button contents. */
  ariaLabel?: string;
  /** Render at reduced emphasis: the option is selectable, but picking it
   *  won't tell you much. Used for muscle-map windows longer than the
   *  lifter's history, where 6M / 1Y / ALL are the same answer three times.
   *
   *  Deliberately NOT `disabled`. Blocking the tap would strand anyone whose
   *  stored preference is one of these, and the point is to set expectations,
   *  not to withhold a view. Tapping it and seeing why is a fine outcome. */
  muted?: boolean;
};

type SegmentedPickerProps<T extends string | number | boolean | null> = {
  /** Semantic group label — required for screen readers
   *  (`aria-label` on the radiogroup wrapper). */
  ariaLabel: string;
  /** All options in display order. */
  options: ReadonlyArray<SegmentedPickerOption<T>>;
  /** Currently-selected value (controlled). */
  value: T;
  /** Fired when the user picks a different option. The component
   *  does NOT fire when the user re-taps the active option (no-op
   *  taps don't reach the caller). */
  onSelect: (next: T) => void;
  /** Server-action pending state — typically from `useTransition`.
   *  Disables the active option (so a double-tap can't double-fire);
   *  inactive options stay tappable so the user can switch again
   *  during a slow in-flight write. */
  pending: boolean;
  /** Layout shape. `flex` (default) lays out options as a single
   *  horizontal row. `grid-3` lays them out in a 3-column grid for
   *  pickers with many options (e.g. the 9-choice default-rest
   *  picker) — predictable wrap instead of an awkward horizontal
   *  flex-wrap split. `fill` also wraps to ~3 per row but lets each
   *  row's items GROW to fill the width, so a count that doesn't
   *  divide by 3 (e.g. the 5-tier activity picker: 3 + 2) has its
   *  short last row stretch across instead of leaving an empty cell. */
  layout?: "flex" | "grid-2" | "grid-3" | "fill";
  /** Button horizontal padding. `px-4` (default) for 2-3 char
   *  labels (KG/LB, kg/lb, sample-pair). `px-3` for compact /
   *  numeric / single-letter labels (Y/N, "10s", "Off", "7D").
   *  Ignored when `size="compact"` — the compact preset has its
   *  own fixed `px-3 py-1.5` padding tuned for the dashboard
   *  inline density. */
  buttonPadding?: "px-3" | "px-4";
  /** Density preset. v9.151:
   *  - `"standard"` (default): `h-9` + `text-sm` — Settings page
   *    pickers, where the row has space for full-size pills.
   *  - `"compact"`: `px-3 py-1.5` + `text-[11px]` — dashboard /
   *    detail-page inline pills (bodyweight unit pills, sessions
   *    heatmap zoom). About 28-30 px tall vs the 36 px standard.
   *  - `"mini"`: matches `compact`'s height + text (`py-1.5`,
   *    `text-[11px]`) but with tight HORIZONTAL padding (`px-1.5`) +
   *    normal tracking — for the muscle map's row of THREE mode pills
   *    (Volume/Plan/Fatigue) beside the 5-window selector, where eight
   *    segments must share one row on iPhone SE. Same height as compact so the two pickers align + stay
   *    tappable; the reduced width is what buys the single-row fit. */
  size?: "standard" | "compact" | "mini" | "chip";
  /** Extra utility classes appended to every button — for opt-in
   *  styling like `uppercase`, `tabular-nums`, `min-w-[2.5rem]`
   *  (single-letter label width floor), or `font-semibold` (heavier
   *  weight; Tailwind's font-weight utility order means
   *  `font-semibold` overrides the base `font-medium`). */
  buttonExtras?: string;
  /** When true, the picker stretches to fill its parent's width and
   *  each option button gets `flex-1` so options distribute evenly.
   *  Defaults to false (the legacy behaviour — picker sizes to its
   *  content). Use for surfaces where the picker needs to read as a
   *  full-width control inside a form column (e.g. the onboarding
   *  wizard's "Pick your units" step on iPhone SE, where content-
   *  sized 2-option pickers leave too much empty space to the
   *  right). Has no effect when `layout="grid-3"` — grid columns
   *  already fill their container. */
  fullWidth?: boolean;
};

export function SegmentedPicker<
  const T extends string | number | boolean | null,
>({
  ariaLabel,
  options,
  value,
  onSelect,
  pending,
  layout = "flex",
  buttonPadding = "px-4",
  size = "standard",
  buttonExtras,
  fullWidth = false,
}: SegmentedPickerProps<T>) {
  // Container shape: flex single-row (default) vs 3-column grid for
  // pickers with too many options for a single-row flex (would either
  // truncate or wrap awkwardly). fullWidth flips the flex variant
  // from content-sized (shrink-0) to fill-width — used by the
  // onboarding wizard for pickers that need to span the form column.
  /** See the note on sizeClass below: the chip size fills its selected
   *  segment and darkens its track, and nothing else does. */
  const chip = size === "chip";

  const containerClass =
    layout === "grid-2"
      ? // Two even columns, so a four-option picker reads as a 2x2 block.
        // `fill` would wrap those four as 3-over-1, which is right for a
        // 5-or-6 option picker (activity, pace) and wrong for a 4 - it makes
        // the last option look like an afterthought rather than a peer.
        "grid grid-cols-2 rounded-md border border-border bg-surface-2 overflow-hidden text-sm"
      : layout === "grid-3"
        ? "grid grid-cols-3 rounded-md border border-border bg-surface-2 overflow-hidden text-sm"
        : layout === "fill"
          ? "flex flex-wrap w-full rounded-md border border-border bg-surface-2 overflow-hidden text-sm"
          : fullWidth
            ? "flex flex-wrap w-full rounded-md border border-border bg-surface-2 overflow-hidden text-sm"
            : "flex shrink-0 rounded-md border border-border bg-surface-2 overflow-hidden text-sm";

  // Button display: `flex flex-1` for grid cells AND for fullWidth
  // flex (so options distribute evenly across the container).
  // `inline-flex` for content-sized flex layouts — buttons size to
  // their text. Without this distinction, grid-layout pickers would
  // render buttons that shrink to their text width and leave empty
  // cell space around the active fill.
  // `fill` adds a ~30% flex-basis on top of flex-1: 30% packs 3 per row
  // (3 × 30% = 90% fits, a 4th wraps), and flex-grow then stretches each
  // row's items to fill 100% — so a 5-item picker renders 3 (⅓ each) over
  // 2 (½ each) with no dangling empty cell.
  // Chip's track is one step darker than the others', so its lighter active
  // fill reads as raised rather than as a dent. Rewritten rather than
  // branched into all six containerClass arms above, which would be six
  // places to keep in step.
  const trackClass = chip
    ? containerClass
        .replace("bg-surface-2", "bg-surface")
        // INLINE, not block. The default container is `flex`, which is
        // block-level and therefore fills whatever it is in unless the
        // parent happens to be a flex row. Every one of the five
        // hand-rolled switchers this size replaces was `inline-flex`,
        // because a unit switcher hugs its two or three words - and a
        // chip that silently stretched to 370px the first time someone
        // put it in a plain div would be a poor advert for consolidating.
        .replace(/(^|\s)flex(\s)/, "$1inline-flex$2")
    : containerClass;

  const buttonDisplay =
    layout === "fill"
      ? "flex flex-1 basis-[30%]"
      : layout === "grid-2" || layout === "grid-3" || fullWidth
        ? "flex flex-1"
        : "inline-flex";

  // Size preset — see prop JSDoc. Compact uses a fixed `px-3 py-1.5
  // text-[11px]` calibrated to match the pre-v9.151 dimensions of
  // the retired BodyweightUnitPills `compact` mode (so the dashboard
  // widget and bodyweight detail page stay pixel-identical post-
  // migration). Same dimensions are also used by the Sessions
  // heatmap zoom pills (`session-heatmap.tsx`, hand-rolled — not
  // yet migrated to this component). Standard keeps the v9.12
  // Settings-densification `h-9` floor and lets buttonPadding
  // choose `px-3` vs `px-4`.
  // tracking-wider lives per-size (not in the shared button class below) so
  // the `mini` preset can use tighter normal tracking to save horizontal
  // space — letting it share a row with a compact picker on small phones.
  const sizeClass =
    size === "mini"
      ? "px-1.5 py-1.5 text-[11px] tracking-normal"
      : size === "chip"
        ? "h-7 px-2.5 text-xs font-semibold uppercase tracking-wider"
        : size === "compact"
          ? "px-3 py-1.5 text-[11px] tracking-wider"
          : `h-9 ${buttonPadding} text-sm tracking-wider`;
  /**
   * THE CHIP SIZE FILLS ITS SELECTED SEGMENT. Nothing else does.
   *
   * The house selected-state is white text and a 2px orange underline, and
   * at 38px on a settings page that is plenty. At 30px tucked into a workout
   * header beside a Finish button it is not: an underline two pixels tall,
   * under two letters, competing with a brand-filled button. Every
   * hand-rolled small switcher in this app - and there were five - solved
   * that the same way, by also filling the selected segment.
   *
   * So the fill is real and it is needed. What is dangerous is making it a
   * FREE CHOICE: the moment a component can look two ways, call sites start
   * picking, and five copies of this control is what that looks like.
   *
   * It is therefore welded to the size and there is no prop for it. Picking
   * `chip` has already picked the fill. There is nothing to remember and
   * nothing to get wrong, and the rule is one line: standard and compact
   * underline, chip underlines AND fills.
   *
   * The track flips with it for the same reason. Chip sits ON content, so
   * its track is `bg-surface` and its fill `bg-surface-2` - the fill is
   * LIGHTER than the track, which is what makes it read as raised. The other
   * sizes keep the surface-2 track they have.
   *
   * (Declared above containerClass, not here, because trackClass reads it.)
   */

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={trackClass}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            // `String()` covers all permitted T (string|number|boolean|null);
            // distinct values produce distinct strings.
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.ariaLabel}
            onClick={() => {
              // No-op on the active option — matches the original
              // hand-rolled behaviour (no spurious server writes
              // when re-tapping the current selection).
              if (!active) onSelect(opt.value);
            }}
            disabled={pending && active}
            className={
              sizeClass +
              " " +
              buttonDisplay +
              " items-center justify-center font-medium transition-colors " +
              (buttonExtras ? buttonExtras + " " : "") +
              // v9.147 — both branches include `border-b-2` so the
              // active/inactive buttons stay the same height (only
              // the border COLOR changes between brand and transparent).
              (active
                ? "text-fg border-b-2 border-brand" + (chip ? " bg-surface-2" : "")
                : "text-fg-muted hover:text-fg border-b-2 border-transparent") +
              // Muted only while INACTIVE: once selected it has to stay
              // readable, or the user can't see what they're looking at.
              (opt.muted && !active ? " opacity-40" : "")
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
